import React, { useEffect, useState } from "react";
import { LoaderCircle } from "lucide-react";
import API_CONFIG from "../../../lib/ApiConfig";
import * as Dialog from "@radix-ui/react-dialog";
import { useAuth } from "../../../hooks/useAuth";
import { OMIGButton, OMIGCard } from "../OMIGScreenWrapper";

const OMIGBuyerDetailsModal = ({ buyerId, customer, onDismiss }) => {
  const { getIdToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dataBuyer, setDataBuyer] = useState(null);
  const [currentTab, setCurrentTab] = useState("details");
  const [buyerDiscount, setBuyerDiscount] = useState(null);
  const [earningsDescription, setEarningsDescription] = useState("");
  const [dataBuyerDescription, setDataBuyerDescription] = useState("");

  useEffect(() => {
    if (!buyerId) return;

    let cancelled = false;

    const fetchBuyerDataAndDiscounts = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = await getIdToken();

        const headers = {
          "Content-Type": "application/json",
        };

        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
          headers["x-user-token"] = token;
        }

        // 1) Load data-buyer core info
        const buyerUrl = API_CONFIG.getUrl(`data-buyers/${buyerId}`);
        const buyerRes = await fetch(buyerUrl, {
          method: "GET",
          headers,
        });

        if (!buyerRes.ok) {
          throw new Error("Failed to load buyer details");
        }

        const buyerData = await buyerRes.json();
        if (cancelled) return;

        setDataBuyer(buyerData || null);
        setDataBuyerDescription(buyerData?.description || "");
        setEarningsDescription(buyerData?.earningsDescription || "");

        // 2) Load discounts for this buyer & customer (mirrors Vue getDiscount)
        const query = new URLSearchParams();
        if (customer?.state) query.append("state", customer.state);
        if (customer?.id) query.append("customerId", customer.id);

        const discountsPath = query.toString()
          ? `data-buyers/${buyerId}/discounts?${query.toString()}`
          : `data-buyers/${buyerId}/discounts`;
        const discountsUrl = API_CONFIG.getUrl(discountsPath);

        const discountsRes = await fetch(discountsUrl, {
          method: "GET",
          headers,
        });

        if (!cancelled && discountsRes.ok) {
          const discountsData = await discountsRes.json();
          if (Array.isArray(discountsData) && discountsData.length) {
            setBuyerDiscount(discountsData[0]);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error loading buyer details:", err);
          setError(err.message || "Failed to load buyer details");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchBuyerDataAndDiscounts();

    return () => {
      cancelled = true;
    };
  }, [buyerId, customer, getIdToken]);

  const shopNowLink =
    dataBuyer?.shopNowLink || buyerDiscount?.buyer?.shopNowLink || null;

  const renderMedia = () => {
    if (dataBuyer?.videoUrl) {
      return (
        <div className="w-full rounded-md overflow-hidden bg-black">
          <video
            className="w-full max-h-64 object-cover"
            src={dataBuyer.videoUrl}
            muted
            controls
            autoPlay
          />
        </div>
      );
    }

    const logoSrc =
      buyerDiscount?.buyer?.logo ||
      buyerDiscount?.buyer?.listLogo ||
      dataBuyer?.logo ||
      dataBuyer?.listLogo;

    if (!logoSrc) {
      return <div className="w-full h-32 md:h-40 rounded-md bg-crimsonGlory" />;
    }

    return (
      <div className="w-full h-32 md:h-40 rounded-md bg-crimsonGlory flex items-center justify-center">
        <img
          src={logoSrc}
          alt={buyerDiscount?.buyer?.name || dataBuyer?.name || "Buyer logo"}
          className="max-h-12 md:max-h-16 w-auto object-contain drop-shadow-md"
        />
      </div>
    );
  };

  let content;

  if (loading) {
    content = (
      <div className="flex flex-col items-center justify-center py-10 gap-3">
        <LoaderCircle className="w-8 h-8 text-crimsonGlory animate-spin" />
        <p className="text-sm text-veryDarkGrey">Loading buyer details...</p>
      </div>
    );
  } else if (error) {
    content = (
      <div className="space-y-4">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onDismiss}
            className="text-sm text-crimsonGlory underline underline-offset-2"
          >
            Close
          </button>
        </div>
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  } else {
    content = (
      <div className="space-y-4">
        {/* Toolbar with close button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onDismiss}
            className="text-sm text-crimsonGlory underline underline-offset-2"
          >
            Close
          </button>
        </div>

        {/* Top media: video or logo + top-right Shop now */}

        {shopNowLink && (
          <a
            href={shopNowLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm flex justify-end font-semibold tracking-wide uppercase text-crimsonGlory hover:text-crimsonGlory/80"
          >
            Shop now
          </a>
        )}

        <div>{renderMedia()}</div>

        {/* Tabs */}
        <div className="border-b border-gray-200 flex text-xs md:text-sm font-semibold tracking-[0.14em] uppercase">
          <button
            type="button"
            className={`flex-1 py-3 text-center ${
              currentTab === "details"
                ? "border-b-2 border-crimsonGlory text-crimsonGlory"
                : "text-gray-400"
            }`}
            onClick={() => setCurrentTab("details")}
          >
            Details
          </button>
          <button
            type="button"
            className={`flex-1 py-3 text-center ${
              currentTab === "earnings"
                ? "border-b-2 border-crimsonGlory text-crimsonGlory"
                : "text-gray-400"
            }`}
            onClick={() => setCurrentTab("earnings")}
          >
            Earnings
          </button>
        </div>

        {/* Tab content */}
        <div className="mt-4 text-sm text-veryDarkGrey leading-relaxed text-center overflow-auto h-[355px]">
          {currentTab === "details" ? (
            <div
              className="data-buyer-description"
              dangerouslySetInnerHTML={{
                __html: dataBuyerDescription || "",
              }}
            />
          ) : (
            <>
              <div
                className="earnings-description"
                dangerouslySetInnerHTML={{
                  __html: earningsDescription || "",
                }}
              />

              {shopNowLink && (
                <div className="mt-6 flex justify-center">
                  <a
                    href={shopNowLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex"
                  >
                    <OMIGButton className="!w-auto px-6 py-2 text-sm">
                      Shop now
                    </OMIGButton>
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <Dialog.Root
      defaultOpen
      onOpenChange={(open) => {
        if (!open && onDismiss) {
          onDismiss();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Title />
        <Dialog.Description />
        <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="max-w-xl w-full mx-4">
            <OMIGCard className="max-h-[80vh] min-h-[260px]">
              {content}
            </OMIGCard>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default OMIGBuyerDetailsModal;
